import type { ReactNode } from 'react'

import { Badge, HelpHint, type StatusTone } from '@aeris/ui'

/**
 * A legenda das situações de uma lista — F156.
 *
 * O "?" na barra, para quem quer entender o conjunto em vez de um selo por vez.
 * O tooltip do selo responde "o que é este aqui"; isto responde "quais existem
 * e o que cada um quer dizer" — que é a pergunta de quem está aprendendo a
 * tela, e a que o cliente da loja fez.
 *
 * Construído sobre o `HelpHint` que já existe (F055): botão de verdade, abre no
 * hover, no foco e no toque, com o conteúdo duplicado em `sr-only` porque o
 * conteúdo do tooltip do Radix só existe no DOM enquanto aberto. Um segundo "?"
 * com comportamento próprio seria exatamente a deriva que a F055 fechou.
 *
 * A fonte é a mesma dos selos e do glossário. Uma legenda que repetisse as
 * frases à mão seria a quinta cópia do vocabulário, e a primeira a ficar velha.
 */

export type LegendEntry = {
  readonly label: string
  readonly tone: StatusTone
  readonly icon: ReactNode
  readonly what: string
  /** "só em pedido" — quando a situação não vale para todo tipo de documento. */
  readonly only?: string | null
}

export const StatusLegend = ({
  entries,
  label = 'as situações desta lista',
}: {
  entries: readonly LegendEntry[]
  label?: string
}): ReactNode => (
  <HelpHint label={label} width="panel">
    <dl className="flex flex-col gap-2">
      {entries.map((entry) => (
        <div key={entry.label} className="flex flex-col gap-0.5">
          <dt>
            <Badge tone={entry.tone} icon={entry.icon}>
              {entry.label}
            </Badge>
            {entry.only == null ? null : (
              <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
                {entry.only}
              </span>
            )}
          </dt>
          <dd className="text-(length:--text-xs) text-(--text-secondary)">
            {entry.what}
          </dd>
        </div>
      ))}
    </dl>
  </HelpHint>
)
